import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FlashCard from './Flashcard';
import { useWouldYouRatherQuestions } from '../../hooks/useGameQuestions';
import { integratedLanguageService } from '../../services/integratedLanguageService';
import { loadLanguage } from '../../lib/i18n';
import { Flag } from 'lucide-react';
import ReportModal from '../modals/ReportModal';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../lib/SettingsContext';

// Button-58 CSS styles with snake border animation for WouldYouRather
const wouldYouRatherButtonStyles = `
  .would-you-rather-button {
    padding: 12px 26px;
    border: 0;
    font-size: 16px;
    transition: all 150ms ease-in-out;
    border-radius: 8px;
    font-weight: 600;
    color: #fff;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    background: #884FEC !important;
    position: relative;
    z-index: 1;
  }

  .would-you-rather-button:hover {
    filter: brightness(1.1);
    background: #7430e8 !important;
  }

  .would-you-rather-button:active {
    background: #7430e8 !important;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
  }

  .would-you-rather-button.selected {
    background: #7430e8 !important;
  }

  .would-you-rather-button.non-selected {
    background: #884FEC !important;
  }

  /* Override any conflicting styles */
  .would-you-rather-button[style*="background"] {
    background: #884FEC !important;
  }

  .would-you-rather-button:hover[style*="background"] {
    background: #7430e8 !important;
  }

  .would-you-rather-button.selected[style*="background"] {
    background: #7430e8 !important;
  }

  .would-you-rather-button.non-selected[style*="background"] {
    background: #884FEC !important;
  }

  .would-you-rather-snake-border {
    position: relative;
    overflow: hidden;
  }



  .would-you-rather-snake-border span:nth-child(1) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(to right, #FFD700, #FFED4E, #FFD700);
    animation: animate1 1.25s linear infinite;
    z-index: 3;
    box-shadow: 0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700;
  }

  @keyframes animate1 {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  .would-you-rather-snake-border span:nth-child(2) {
    position: absolute;
    top: 0;
    right: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(to bottom, #FFD700, #FFED4E, #FFD700);
    animation: animate2 1.25s linear infinite;
    animation-delay: 0.625s;
    z-index: 3;
    box-shadow: 0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700;
  }

  @keyframes animate2 {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100%); }
  }

  .would-you-rather-snake-border span:nth-child(3) {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(to left, #FFD700, #FFED4E, #FFD700);
    animation: animate3 1.25s linear infinite;
    z-index: 3;
    box-shadow: 0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700;
  }

  @keyframes animate3 {
    0% { transform: translateX(100%); }
    100% { transform: translateX(-100%); }
  }

  .would-you-rather-snake-border span:nth-child(4) {
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(to top, #FFD700, #FFED4E, #FFD700);
    animation: animate4 1.25s linear infinite;
    animation-delay: 0.625s;
    z-index: 3;
    box-shadow: 0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700;
  }

  @keyframes animate4 {
    0% { transform: translateY(100%); }
    100% { transform: translateY(-100%); }
  }
`;

type SupportedLanguage = 'en' | 'es' | 'fr' | 'hi' | 'de';

interface WouldYouRatherGameCardProps {
  isVisible: boolean;
  onComplete?: (gameResultCandidate?: string) => void;
  spinnerDimension?: number; // Use this for sizing!
  winnerName?: string;
  language?: SupportedLanguage; // New prop for language support
}

const WouldYouRatherGameCard: React.FC<WouldYouRatherGameCardProps> = ({ 
  isVisible, 
  onComplete,
  spinnerDimension = 400,
  winnerName = "Winner",
  language // New prop
}) => {

  const [isFlipped, setIsFlipped] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  // Question state now managed by CDN hook
  // Loading state now managed by CDN hook
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<'report' | null>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isFadingIn, setIsFadingIn] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSnakeBorder, setShowSnakeBorder] = useState(false);
  const { t, i18n, ready } = useTranslation();
  const { user } = useAuth();
  const { audioEnabled } = useSettings();
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('en');
  const prevLanguageRef = React.useRef<SupportedLanguage | null>(null);

  // CDN-based question management - following Trivia's exact pattern
  const {
    currentQuestion: cdnQuestion,
    getNextQuestion,
    isLoading: isCDNLoading,
    error: questionError,
    initialize: initializeQuestions,
    forceReload,
    stats
  } = useWouldYouRatherQuestions(currentLanguage, user?.uid || null, isVisible);

  // Inject button-58 styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = wouldYouRatherButtonStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set current language
  useEffect(() => {
    const lang = language || 'en'; // Default to English
    setCurrentLanguage(lang);
  }, [language]);

  // Handle language change
  useEffect(() => {
    const lang = language || 'en';
    if (prevLanguageRef.current && prevLanguageRef.current !== lang) {
      // Language changed, use integrated service to handle both translations and game data
      integratedLanguageService.changeLanguage(lang, 'wouldYouRather').catch(error => {
        console.error('Error changing language in WouldYouRatherGameCard:', error);
      });
    }
    prevLanguageRef.current = lang;
  }, [currentLanguage, language]);

  // Ensure language translations are loaded
  useEffect(() => {
    const lang = language || 'en';
    if (lang !== 'en') {
      loadLanguage(lang).catch(error => {
        console.error('Error loading language translations:', error);
      });
    }
  }, [language]);

  // Calculate card size with standard aspect ratio and responsive scaling
  let scale = windowWidth <= 768 ? 1.3 : 1.1;
  // Responsive: use min of viewport width/height for card size
  const maxCardWidth = Math.min(window.innerWidth * 0.95, 400);
  const maxCardHeight = Math.min(window.innerHeight * 0.95, 400);
  // Use a 1.5 aspect ratio, but never exceed viewport
  let cardWidth = maxCardWidth;
  let cardHeight = cardWidth / 1.5;
  if (cardHeight > maxCardHeight) {
    cardHeight = maxCardHeight;
    cardWidth = cardHeight * 1.5;
  }
  // Make card 20% bigger for tablet/desktop
  if (windowWidth > 768) {
    cardWidth = cardWidth * 1.2;
    cardHeight = cardHeight * 1.2;
  }
  const fontSize = cardWidth * 0.045;

  const handleChoice = (choiceType: 'option1' | 'option2', event: React.MouseEvent) => {
    // Prevent event from bubbling up to card flip handler
    event.stopPropagation();
    
    if (!cdnQuestion) return;
    
    const choice = cdnQuestion[choiceType];
    setHasSelected(true);
    setSelectedChoice(choice);
    setShowSnakeBorder(true);
    
    // Play winner sound (no loop)
    if (audioEnabled) {
      const audio = new Audio('/sounds/winner.mp3');
      audio.volume = 0.3;
      audio.play().catch(e => console.error('Audio play failed:', e));
    }
    
    // Start fade-out after showing choice for 5 seconds
    setTimeout(() => {
      setIsFadingOut(true);
      // Complete the fade-out and call onComplete after animation
      setTimeout(() => {
        if (onComplete) {
          onComplete('🟢'); // Green dot for choice made
        }
      }, 500); // 500ms fade-out duration
    }, 5000);
  };

  // Reset card state and fetch new question when it becomes visible
  useEffect(() => {
    if (isVisible) {
      console.log(`🎮 [WouldYouRatherGameCard] Card became visible, fetching new question for language: ${currentLanguage}`);
      
      setIsFlipped(false); // Start with red card (winner name) side
      setHasSelected(false);
      setSelectedChoice(null);
      setIsFadingOut(false);
      setIsFadingIn(true);
      setSelectedAction(null); // Reset action selection
      setShowReportModal(false); // Reset report modal
      
      // Follow Trivia's exact pattern
      fetchNewQuestion();
      
      // Complete fade-in after animation
      setTimeout(() => {
        setIsFadingIn(false);
        // No automatic flip - wait for user click
      }, 300); // 300ms fade-in duration
    }
  }, [isVisible, currentLanguage]);

  // Handle report button click
  const handleReportClick = () => {
    setShowReportModal(true);
    setSelectedAction(null); // Reset action selection when opening modal
  };

  // Handle report submission
  const handleReportSubmitted = () => {
    // Report submitted successfully
    // Set the selectedAction to 'report' to show the button as filled
    setSelectedAction('report');
    // You can add any additional logic here, like showing a success message
  };



  // Handle skip button click
  const handleSkipClick = () => {
    // Close the card and allow new spin
    setIsFadingOut(true);
    setTimeout(() => {
      if (onComplete) {
        onComplete('🔘'); // Grey dot for skip
      }
    }, 500); // 500ms fade-out duration
  };

  // Fetch a new question using CDN system - following Trivia's exact pattern
  const fetchNewQuestion = async () => {
    try {
      console.log('🔄 Fetching new question from CDN...');
      console.log('📊 Current stats:', stats);
      console.log('🔍 Manager state - error:', questionError);
      
      // If nothing is loaded yet, initialize or force reload
      if (!stats || stats.currentQuestions === 0) {
        await initializeQuestions();
      }
      
      let attempts = 0;
      const maxAttempts = 6;
      let picked: any = null;

      while (attempts < maxAttempts) {
        attempts++;
        console.log(`🔄 Attempt ${attempts} - calling getNextQuestion()...`);
        const q = getNextQuestion();
        console.log(`📝 Attempt ${attempts} got:`, q);
        
        // Check for Would You Rather format (option1, option2 properties)
        if (q && q.option1 && q.option2 && q.id) {
          picked = q;
          break;
        }
        
        // If invalid/null, wait a bit and try to ensure next set is prepared
        if (attempts === 3) {
          await forceReload();
        }
        await new Promise(r => setTimeout(r, 250));
      }
      
      if (picked) {
        console.log(`🎮 [WouldYouRatherGameCard] CDN question loaded: ${picked.id} - "${picked.option1}"`);
        // Debug rotation stats after fetching (like Trivia does)
        debugRotationStats();
      } else {
        console.warn('⌛ Questions not ready yet; leaving card in loading state');
      }
    } catch (error) {
      console.error('❌ Error fetching question from CDN:', error);
    }
  };

  // Debug method to check CDN stats
  const debugRotationStats = () => {
    console.log(`🎮 [WouldYouRatherGameCard] CDN stats:`, stats);
  };

  // Auto-hide card after 5 minutes if no choice is made
  useEffect(() => {
    if (isVisible && !hasSelected) {
      const timeoutId = setTimeout(() => {
        setIsFadingOut(true);
        setTimeout(() => {
          if (onComplete) {
            onComplete('🔘'); // Grey dot for timeout
          }
        }, 500); // 500ms fade-out duration
      }, 300000); // 5 minutes timeout

      return () => clearTimeout(timeoutId);
    }
  }, [isVisible, hasSelected, onComplete]);

  // Responsive padding and gap (reduced by 30%)
  const responsivePad = `clamp(5px, 2%, 12px)`;
  const responsiveGap = responsivePad;

  // Action bar height as 10% of card height, with min/max for usability
  const actionBarPct = 0.10;
  const actionBarHeight = Math.max(32, Math.min(cardHeight * actionBarPct, 56));

  // Get translations for current language - use useMemo to recalculate when language changes
  const languageText = React.useMemo(() => ({
    title: t('games.wouldYouRather.title') || 'Would You Rather?',
    loading: t('games.wouldYouRather.loading') || 'Loading question...',
    skip: t('games.wouldYouRather.skip') || 'Skip',
    like: t('games.wouldYouRather.like') || 'Like',
    dislike: t('games.wouldYouRather.dislike') || 'Dislike',
    report: t('games.wouldYouRather.report') || 'Report',
    or: t('games.wouldYouRather.or') || 'OR',
    saving: t('games.wouldYouRather.saving') || 'Saving...'
  }), [t, currentLanguage, i18n.language, ready]);

  // Would You Rather front card - button-19 style with WouldYouRather.png image
  const WouldYouRatherFront = (
    <div 
      onClick={() => {
        // Allow flipping from red card to options
        if (!isFlipped && !hasSelected) {
          setIsFlipped(true);
        }
      }}
      style={{
        width: '100%',
        height: '100%',
        background: '#4b0082',
        border: 0,
        borderRadius: '0.375em',
        boxShadow: '0.2em 0.2em 0.5em rgba(0, 0, 0, 0.47), 0 -0.1em 0 0.1em rgba(0, 0, 0, 0.27), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #370066 inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #370066 inset',
        color: '#fff',
        cursor: !isFlipped && !hasSelected ? 'pointer' : 'default',
        fontSize: Math.max(fontSize * 1.2, 18),
        margin: 0,
        padding: '0.75em 1em',
        textShadow: '0 0 0.2em rgba(255, 255, 255, 0.47)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        position: 'relative',
        boxSizing: 'border-box'
      }}
    >
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        right: '8px',
        bottom: '8px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '0.25em',
        pointerEvents: 'none'
      }}></div>
      <div style={{
        textAlign: 'center',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0px',
        height: '100%',
        justifyContent: 'center'
      }}>
        {/* Winner enclosed in cups */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: Math.max(fontSize * 2.0, 30),
          fontWeight: 'bold',
          color: '#FFD700',
          textShadow: '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.6), 0 0 30px rgba(255, 215, 0, 0.4)',
          filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
          marginBottom: '8px'
        }}>
          <span style={{fontSize: Math.max(fontSize * 1.5, 22.5)}}>🏆</span>
          <span>{winnerName}</span>
          <span style={{fontSize: Math.max(fontSize * 1.5, 22.5)}}>🏆</span>
        </div>

        {/* WouldYouRather.png image with fallback to thinking emoji */}
        <div style={{
          width: Math.max(fontSize * 3.75, 60),
          height: Math.max(fontSize * 3.75, 60),
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
        }}>
          <img 
            src="/images/WouldYouRather.png" 
            alt="Would You Rather"
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,192,203,0.6), 0 0 12px rgba(255,182,193,0.4))'
            }}
            onError={(e) => {
              // Fallback to thinking emoji if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'block';
              }
            }}
          />
          <div style={{
            fontSize: Math.max(fontSize * 2.5, 40),
            display: 'none',
            textShadow: '0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,192,203,0.6), 0 0 12px rgba(255,182,193,0.4)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
          }}>🤔</div>
        </div>
        
        {/* Would You Rather title with custom black spades and white shine */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: Math.max(fontSize * 1.6, 24),
          fontWeight: 'bold'
        }}>
          <span style={{
            color: '#FFFFFF',
            fontSize: Math.max(fontSize * 1.8, 24),
            textShadow: '0 0 4px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.7), 0 0 12px rgba(255,255,255,0.5)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))'
          }}>♦</span>
                          <span>{isCDNLoading ? (languageText.loading || 'Loading question...') : t('sidebar.games.would-you-rather')}</span>
          <span style={{
            color: '#FFFFFF',
            fontSize: Math.max(fontSize * 1.8, 24),
            textShadow: '0 0 4px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.7), 0 0 12px rgba(255,255,255,0.5)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))'
          }}>♦</span>
        </div>
      </div>
    </div>
  );

  // Would You Rather back card with two divisions: options above, action strip below
  const outerBorderColor = '#1a237e'; // dark blue
  const buttonGap = Math.max(cardHeight * 0.06, 12);
  const optionFontSize = Math.max(fontSize * 1.1, 16);
  const optionRadius = 20;
  const optionHeight = (cardHeight - 3 * buttonGap) / 2;
  // Button colors for button-58 style (purple theme)
  const blueBg = '#884FEC';
  const blueBorder = '#884FEC';
  const yellowBg = '#884FEC';
  const yellowBorder = '#884FEC';
  const buttonShadow = '0 6px 24px 0 rgba(153,102,255,0.13), 0 2px 8px 0 rgba(0,0,0,0.10)';
  const blueGradient = '#884FEC';
  const yellowGradient = '#884FEC';
  const reducedPadding = Math.max(cardHeight * 0.03, 10);
  const tightButtonGap = 6;
  
  // OR Pill design - responsive width based on text length
  const orText = languageText.or || 'OR';
  const orTextLength = orText.length;
  const orPillWidth = Math.max(cardWidth * 0.12, 32) + (orTextLength * 6); // Reduced width and padding
  const orPillHeight = Math.max(cardWidth * 0.08, 28);
  const orPillFontSize = Math.max(optionFontSize * 0.9, 14);
  
  // Calculate responsive button height based on card height
  const buttonHeight = Math.max(Math.min(cardHeight * 0.15, 45), 32); // Between 32px and 45px
  
  const WouldYouRatherBack = (
    <div
      onClick={(e) => {
        e.stopPropagation();
        // Prevent flipping back to red card once options are shown
        return;
      }}
      style={{
        width: '100%',
        height: '100%',
        background: '#deb3ff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        fontWeight: 'bold',
        fontSize: Math.max(fontSize * 0.8, 14),
        color: '#333',
        borderRadius: `${optionRadius + 4}px`,
        position: 'relative',
        boxSizing: 'border-box',
        border: `3px solid ${outerBorderColor}`,
        boxShadow: '0 8px 32px 0 rgba(75,0,130,0.10), 0 2px 8px 0 rgba(0,0,0,0.08)',
        minHeight: 220,
        overflow: 'hidden',
      }}
    >
      {/* Options Section fills all space above action bar */}
      <div style={{
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: responsivePad,
        gap: responsiveGap,
      }}>
        <button
          style={{
            flex: '1 1 0',
            minHeight: 0,
            width: '100%',
            boxSizing: 'border-box',
            cursor: hasSelected ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0,
                            opacity: hasSelected && selectedChoice !== cdnQuestion?.option1 ? 0.4 : 1,
            pointerEvents: 'auto',
            minWidth: 0,
            userSelect: 'none',
            whiteSpace: 'unset',
            textDecoration: 'none',
            outline: 0,
            WebkitUserSelect: 'none',
            touchAction: 'manipulation',
                            background: selectedChoice === cdnQuestion?.option1 ? '#7430e8' : '#884FEC',
            color: '#fff',
            border: '2px solid #4A148C',
            borderRadius: '8px',
            fontWeight: '600',
            boxShadow: '0 6px 12px -2px rgba(74, 20, 140, 0.3), 0 4px 8px -1px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            transition: 'all 150ms ease-in-out',
          }}
                          className={`would-you-rather-button ${selectedChoice === cdnQuestion?.option1 ? 'selected' : 'non-selected'} ${showSnakeBorder && selectedChoice === cdnQuestion?.option1 ? 'would-you-rather-snake-border' : ''}`}
          onMouseEnter={() => !hasSelected && setHoveredBtn('option1')}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={(e) => !hasSelected && handleChoice('option1', e)}
        >
                          {showSnakeBorder && selectedChoice === cdnQuestion?.option1 && (
            <>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </>
          )}
                    <span style={{
            padding: '2px 0',
            position: 'relative',
            zIndex: 1,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            textAlign: 'center',
            width: '100%',
            fontSize: `clamp(14px, ${optionFontSize * 1.0}px, 2.2vw)`,
            lineHeight: 1.3,
            overflow: 'hidden',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
          }}>
            {isCDNLoading ? '...' : cdnQuestion?.option1 || t('common.option1', 'Option 1')}
          </span>
        </button>
        {/* OR Pill - absolutely positioned over the border between buttons */}
        <div style={{
          width: '100%',
          height: 0,
          position: 'relative',
        }}>
          <div style={{
            width: orPillWidth,
            height: orPillHeight,
            background: '#4A148C',
            color: '#fff',
            borderRadius: `${orPillHeight / 2}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: orPillFontSize,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.18)',
            zIndex: 3,
            border: '2.5px solid #fff',
            position: 'absolute',
            left: '50%',
            top: `-${orPillHeight / 2}px`,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            padding: '0 4px',
          }}>
            {orText}
          </div>
        </div>
        <button
          style={{
            flex: '1 1 0',
            minHeight: 0,
            width: '100%',
            boxSizing: 'border-box',
            cursor: hasSelected ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0,
                            opacity: hasSelected && selectedChoice !== cdnQuestion?.option2 ? 0.4 : 1,
            pointerEvents: 'auto',
            minWidth: 0,
            userSelect: 'none',
            whiteSpace: 'unset',
            textDecoration: 'none',
            outline: 0,
            WebkitUserSelect: 'none',
            touchAction: 'manipulation',
                            background: selectedChoice === cdnQuestion?.option2 ? '#7430e8' : '#884FEC',
            color: '#fff',
            border: '2px solid #4A148C',
            borderRadius: '8px',
            fontWeight: '600',
            boxShadow: '0 6px 12px -2px rgba(74, 20, 140, 0.3), 0 4px 8px -1px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            transition: 'all 150ms ease-in-out',
          }}
                          className={`would-you-rather-button ${selectedChoice === cdnQuestion?.option2 ? 'selected' : 'non-selected'} ${showSnakeBorder && selectedChoice === cdnQuestion?.option2 ? 'would-you-rather-snake-border' : ''}`}
          onMouseEnter={() => !hasSelected && setHoveredBtn('option2')}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={(e) => !hasSelected && handleChoice('option2', e)}
        >
                          {showSnakeBorder && selectedChoice === cdnQuestion?.option2 && (
            <>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </>
          )}
                    <span style={{
            padding: '2px 0',
            position: 'relative',
            zIndex: 1,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            textAlign: 'center',
            width: '100%',
            fontSize: `clamp(14px, ${optionFontSize * 1.0}px, 2.2vw)`,
            lineHeight: 1.3,
            overflow: 'hidden',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
          }}>
            {isCDNLoading ? '...' : cdnQuestion?.option2 || t('common.option2', 'Option 2')}
          </span>
        </button>
      </div>
              {/* Action Bar (10% of card height, min/max for usability) */}
        <div
          style={{
            flex: `0 0 ${actionBarHeight}px`,
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            boxSizing: 'border-box',
            gap: 0,
            borderTop: '1.5px solid #e5e7eb',
            background: '#f0e8fc',
            position: 'relative',
            minHeight: actionBarHeight,
            maxHeight: actionBarHeight,
            zIndex: 10,
          }}
        >
        {/* Left: Action buttons */}
        {/* Left: Slim Report button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <button
            onClick={handleReportClick}
            style={{
              background: '#e5e7eb',
              border: '1px solid #9ca3af',
              borderRadius: '4px',
              color: '#374151',
              fontSize: '0.75rem',
              cursor: 'pointer',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              transition: 'all 0.2s',
              padding: '3px 6px',
              minWidth: '40px',
              minHeight: '20px',
            }}
            title={t('common.report') || 'Report'}
          >
            <Flag 
              size={12} 
              style={{ 
                fill: selectedAction === 'report' ? '#dc2626' : 'none',
                stroke: selectedAction === 'report' ? '#dc2626' : '#374151'
              }} 
            />
            <span style={{ fontWeight: '400', fontSize: '0.7rem' }}>
              {t('common.report') || 'Report'}
            </span>
          </button>
        </div>

        {/* Right: Skip button */}
        <button
          onClick={hasSelected ? undefined : handleSkipClick}
          style={{
            background: 'none',
            border: 'none',
            color: hasSelected ? '#9ca3af' : '#2563eb',
            fontSize: `clamp(12px, ${fontSize * 0.9}px, 1.6vw)`,
            cursor: hasSelected ? 'not-allowed' : 'pointer',
            padding: '6px 8px',
            borderRadius: '6px',
            fontWeight: 600,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            opacity: hasSelected ? 0.5 : 1,
          }}
          title={hasSelected ? t('common.choiceMade', 'Choice already made') : t('common.skip', 'Skip')}
        >
          <span style={{fontWeight:'bold',fontSize:'1.3em',verticalAlign:'middle'}}>⏭</span>
          {t('common.skip', 'Skip')}
        </button>
      </div>
    </div>
  );

  if (!isVisible) return null;
  
  // Wait for translations to be ready
  if (!ready) {
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        color: 'white',
        fontSize: '16px'
      }}>
        {t('common.loadingTranslations', 'Loading translations...')}
      </div>
    );
  }

  return (
    <>
      {/* Modal overlay to block background interaction */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 999,
          pointerEvents: 'auto',
          transition: 'opacity 0.3s ease-out',
          opacity: isFadingOut ? 0 : 1,
        }}
      />
      
      {/* Saving indicator removed - no longer showing to users */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: cardWidth,
        height: cardHeight,
        minWidth: 180,
        minHeight: 180,
        maxWidth: '95vw',
        maxHeight: '95vh',
        zIndex: 1000,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
      className={isFadingIn ? 'card-fade-in' : isFadingOut ? 'card-fade-out' : ''}
      >
        <style>
          {`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.8);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
            }
            
            @keyframes fadeOut {
              from {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
              to {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.8);
              }
            }
            
            .card-fade-in {
              animation: fadeIn 0.3s ease-out forwards;
            }
            
            .card-fade-out {
              animation: fadeOut 0.5s ease-in forwards;
            }
          `}
        </style>
        <FlashCard 
          front={WouldYouRatherFront} 
          back={WouldYouRatherBack}
          isFlipped={isFlipped}
          onFlip={() => {
            // Allow flipping from red card to options
            if (!hasSelected) {
              setIsFlipped(!isFlipped);
            }
          }}
          width={cardWidth}
          height={cardHeight}
        />
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        gameType="wouldYouRather"
                    questionId={cdnQuestion?.id}
            questionText={cdnQuestion ? `${cdnQuestion.option1} OR ${cdnQuestion.option2}` : ''}
        onReportSubmitted={handleReportSubmitted}
      />

      {/* CDN Stats Overlay - Show only in development and localhost */}
      {(process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') && (
        <div style={{
          position: 'fixed',
          top: '64px', // 54px header height + 10px margin
          right: '10px',
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px',
          fontFamily: 'monospace',
          zIndex: 2000,
          maxWidth: '300px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>CDN Stats:</div>
          <div>Current Questions: {stats?.currentQuestions ?? 0}</div>
          <div>Used Questions: {stats?.usedQuestions ?? 0}</div>
          <div>Prefetch Block: {stats?.nextPrefetchBlock ?? '-'}</div>
          <div>Current Block: {stats?.currentBlock ?? 0}</div>
          <div>Total Blocks: {stats?.totalBlocks ?? 0}</div>
          {stats?.error && <div style={{ color: '#ff6b6b' }}>Error: {stats.error}</div>}
        </div>
      )}
    </>
  );
};

export default WouldYouRatherGameCard; 